import type { ReactNode } from 'react';

/**
 * Small shared presentational pieces, kept in one place to stay consistent.
 *
 * This file carries almost the whole visual identity: the pages compose these
 * and rarely name a colour themselves, which is what let the console take on
 * the StoneLink glass look as a theme change rather than a rewrite.
 */

export function Card({
  title,
  description,
  actions,
  children,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="glass">
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            {title && <h2 className="font-display text-2xl">{title}</h2>}
            {description && <p className="mt-1 text-sm text-muted">{description}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Button({
  children,
  variant = 'primary',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
}) {
  // `primary` and `secondary` defer to the shared .btn primitives so the
  // console's buttons are literally the marketing site's buttons.
  const styles = {
    primary: 'btn btn-primary',
    secondary: 'btn',
    danger:
      'inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-red-400/40 bg-red-500/10 px-4 text-sm font-medium text-red-300 transition hover:bg-red-500/20',
    ghost:
      'inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3.5 text-sm font-medium text-muted transition hover:text-ink',
  }[variant];

  return (
    <button
      {...props}
      className={`focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50 ${styles} ${props.className ?? ''}`}
    >
      {children}
    </button>
  );
}

const FIELD =
  'w-full rounded-xl border border-line bg-white/5 px-3 text-sm text-ink outline-none transition focus:border-brand focus:bg-white/8 focus:ring-2 focus:ring-brand/25';

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`h-11 ${FIELD} ${props.className ?? ''}`} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea {...props} className={`py-2.5 leading-relaxed ${FIELD} ${props.className ?? ''}`} />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`h-11 ${FIELD} ${props.className ?? ''}`} />;
}

/** The eyebrow treatment: mono, uppercase, widely tracked. */
export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block font-mono text-[11px] tracking-[0.18em] text-soft uppercase"
    >
      {children}
    </label>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'warn' | 'good' | 'bad';
}) {
  const styles = {
    neutral: 'border-line bg-white/6 text-soft',
    brand: 'border-brand/35 bg-brand/12 text-brand',
    warn: 'border-amber-300/30 bg-amber-300/10 text-amber-200',
    good: 'border-emerald-300/30 bg-emerald-300/10 text-emerald-200',
    bad: 'border-red-400/30 bg-red-400/10 text-red-300',
  }[tone];
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-0.5 font-mono text-[10.5px] tracking-[0.08em] uppercase ${styles}`}
    >
      {children}
    </span>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-line px-6 py-12 text-center">
      <p className="font-display text-xl">{title}</p>
      {hint && <p className="mt-2 text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function PageHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-7">
      <h1 className="font-display text-4xl leading-none tracking-tight">{title}</h1>
      <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-soft">{description}</p>
    </div>
  );
}

/** The mono eyebrow with the breathing accent dot, for section kickers. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="eyebrow !mb-4">
      <span className="dot" />
      {children}
    </p>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-xl border border-red-400/30 bg-red-400/10 px-3.5 py-2.5 text-sm text-red-200"
    >
      {children}
    </p>
  );
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
